import DetailPanel, { PanelEyebrow } from './detail_panel'
import { panelKey, type PanelTarget } from './panel_navigation'
import { SelectedCountrySwatch, SelectedSwatch, WineryDot, wineryDotPaint } from './plate_key'
import { CHROME_FOCUS_CLASS } from '../../lib/chrome_config'
import { COUNTRY_KIND } from '../../lib/map_config'

interface PendingPanelProps {
  /** What was chosen. Its name and kind are whatever the shell already knew. */
  target: PanelTarget
  /** The page did not arrive. The frame stays and says so. */
  failed: boolean
  /**
   * How tall the sheet this stands in for was, in px, when there was one. Null
   * when the stand-in is opening over the bare map.
   */
  held?: number | null
  onRetry: () => void
  onDismiss: () => void
}

// What the eyebrow reads for the two kinds of panel whose kind is the type
// itself. A place's is its own `kind` — AVA, PDO, country — which the feature
// or the search result that was clicked already carries. These are the words
// the two real panels use (`winery_panel.tsx`, `grape_panel.tsx`), so the line
// does not change under the reader when the page lands.
const KIND_BY_TYPE: Record<PanelTarget['type'], string | null> = {
  place: null,
  winery: 'Winery',
  grape: 'Grape variety',
}

/**
 * The mark the page's own panel will open its kind line with: the amber ribbon
 * for a place (`place_panel.tsx`) or the soft band for a country, the ringed
 * dot for a producer
 * (`winery_panel.tsx`), nothing for a grape, which is not on the map. Drawn
 * here too, because it is 20-odd pixels in front of the kind word and a
 * stand-in without it moved that word sideways the moment the page landed.
 *
 * The real panels leave it out for a place with no shape and a producer with
 * no coordinates. The stand-in does the same when the shell could tell
 * (`PanelTarget.onMap`), and otherwise draws it. A producer's dot takes the
 * colour the map is drawing it in when the shell holds that, and the neutral
 * otherwise — a change of colour under the reader is not a change of place.
 */
function standInMark(target: PanelTarget) {
  if (target.onMap === false) return undefined
  if (target.type === 'place') return target.kind === COUNTRY_KIND ? <SelectedCountrySwatch /> : <SelectedSwatch />
  if (target.type !== 'winery') return undefined

  const { color, opacity } = wineryDotPaint(target.dot?.sizeClass, target.dot?.stated ?? true)

  return <WineryDot color={color} opacity={opacity} selected className="mx-1.5" />
}

/**
 * The panel a reader gets the moment they choose something, before its page
 * has arrived.
 *
 * A click on the map used to change nothing until the server answered: no
 * frame, no name, no highlight — a round trip of looking at the place you had
 * just clicked wondering whether you had. Everything needed to answer "yes,
 * that one" is already on the client. The NAME is on the feature that was
 * clicked (or the search result, or the breadcrumb step), so the frame is
 * drawn with it at once; the body is what is still on its way.
 *
 * It is the real shell — `DetailPanel` — so it sits in the dock exactly where
 * the page's own panel will, focuses the way it will and closes the way it
 * does. Its test id is its own, and deliberately none of `place-panel`,
 * `winery-panel` or `grape-panel`: a browser test waits on those for real
 * content.
 *
 * THE BODY STARTS EMPTY, NOT AS A SKELETON. Most pages arrive within a few
 * frames — a hovered one is already in memory — and bars that flash up for a
 * tenth of a second and vanish are a flicker, not feedback; the name appearing
 * is the feedback. The bars fade in only once the wait has become one
 * (`animate-late-in`, 350 ms), which is the same rule every other pending
 * notice on the map keeps.
 *
 * ON A PHONE IT IS AS TALL AS THE SHEET IT STANDS IN FOR (`hold`). A stand-in
 * has a title and three grey bars to say, about 290px of sheet; the page it is
 * waiting for is usually the full 60dvh. So a tap on a winery in a place's
 * list made the sheet DROP by 217px and climb back when the page landed, the
 * locate button and the credit riding down and up with it — 0.17 of layout
 * shift each way, on the one step the stand-in exists to make feel instant.
 * It is held at the height of the sheet that was there; from the bare map,
 * where there was none, at the cap the page's own sheet will most likely
 * reach. A page that turns out shorter still settles once, down — a country
 * with nothing imported is 150px — and that is one move instead of two.
 *
 * When the page does not arrive the frame stays and says so, with the two
 * things there are to do about it. Close puts back whatever was there before.
 */
export default function PendingPanel({ target, failed, held = null, onRetry, onDismiss }: PendingPanelProps) {
  const kind = target.kind ?? KIND_BY_TYPE[target.type]
  const name = target.name ?? null

  return (
    <DetailPanel
      // A screen reader is told a name even when there is none to draw.
      title={name ?? 'Loading'}
      heading={
        name ? undefined : <span className="block h-7 w-2/3 rounded bg-black/5 motion-safe:animate-pulse" />
      }
      focusKey={`pending:${panelKey(target)}`}
      hold={held ? `${Math.round(held)}px` : '60dvh'}
      testId="panel-pending"
      // Always a line, so the title sits where the real panel will set it. A
      // non-breaking space holds the line open when the kind is not known. And
      // the same mark in front of it, so the word sits where it will too.
      eyebrow={
        <PanelEyebrow testId="panel-pending-kind" mark={standInMark(target)}>
          {kind ?? ' '}
        </PanelEyebrow>
      }
    >
      {failed ? (
        // The well with a claret rule down its edge, not an amber card. Amber
        // is the selection's and nothing else's on this plate, and the failure
        // is shown beside the amber outline of the very place that failed.
        <div className="mt-2 rounded-xl border-l-[3px] border-accent bg-well px-4 py-3 text-sm text-ink ring-1 ring-inset ring-ink/[0.06]">
          <p role="alert">Couldn’t load {name ?? 'this'}.</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onRetry}
              className={`min-h-11 rounded-full bg-accent px-4 text-sm font-medium text-white hover:bg-accent/90 md:min-h-10 ${CHROME_FOCUS_CLASS}`}
            >
              Retry
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className={`min-h-11 rounded-full px-4 text-sm font-medium text-ink ring-1 ring-ink/20 hover:bg-black/[0.04] md:min-h-10 ${CHROME_FOCUS_CLASS}`}
            >
              Close
            </button>
          </div>
        </div>
      ) : (
        <>
          <div aria-hidden="true" className="space-y-3 pt-2 motion-safe:animate-late-in">
            <div className="h-3 w-1/3 rounded bg-black/5 motion-safe:animate-pulse" />
            <div className="h-3 w-2/3 rounded bg-black/5 motion-safe:animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-black/5 motion-safe:animate-pulse" />
            <div className="h-24 rounded-xl bg-black/5 motion-safe:animate-pulse" />
          </div>
          <p role="status" className="sr-only">
            Loading {name ?? 'details'}…
          </p>
        </>
      )}
    </DetailPanel>
  )
}
